import { DetailOverlay, DetailOverlaySkeleton } from './DetailShell'
import { IssueDetailBody } from './IssueDetailBody'
import { useGisIssueDetail } from '../../../hooks/useGisIssueDetail'

import type { GisIssueDetail, GisScope, GisSite } from '../../../api/types'
import type { HodOpenRef } from '../../../lib/detail-stack'

interface IssueDetailProps {
  issueId: string
  backLabel: string
  onBack: () => void
  /** District sites — used to resolve the issue's mandal (the detail payload omits it). */
  sites: GisSite[]
  scope: GisScope
  /** Switch to a related issue (from the inline site/department lists) — pushes a new drill frame. */
  onOpenIssue: (id: string, siteId?: string | null) => void
  onOpenHod: (hod: HodOpenRef) => void
  onSelectOfficer: (id: string, name: string) => void
  onSelectMandal: (name: string) => void
}

/** The loaded v2 detail body inside the drill overlay (which owns Back + ref). */
function IssueDetailLoaded({ issue, backLabel, onBack, sites, scope, onOpenIssue, onOpenHod, onSelectOfficer, onSelectMandal }: IssueDetailProps & { issue: GisIssueDetail }) {
  return (
    <DetailOverlay backLabel={backLabel} onBack={onBack} refText={issue.issueRef}>
      <IssueDetailBody issue={issue} sites={sites} scope={scope} onOpenIssue={onOpenIssue} onOpenHod={onOpenHod} onSelectOfficer={onSelectOfficer} onSelectMandal={onSelectMandal} />
    </DetailOverlay>
  )
}

/**
 * The v2 issue detail layer: a site-photo banner, title, severity/status/age badges, the resolution
 * banner (overdue/due-soon only), the Responsibility card, an "At a glance" grid, the description, the
 * resolution timeline, and the inline related-issue lists ("Other issues at this site / in {dept}"),
 * inside a full-panel DetailOverlay. The detail payload omits the mandal — it's resolved from `sites`.
 */
export function IssueDetail(props: IssueDetailProps) {
  const { data: issue, isPending, isError } = useGisIssueDetail(props.issueId)
  if (isPending) {
    return (
      <DetailOverlay backLabel={props.backLabel} onBack={props.onBack}>
        <DetailOverlaySkeleton />
      </DetailOverlay>
    )
  }
  if (isError || !issue) {
    return (
      <DetailOverlay backLabel={props.backLabel} onBack={props.onBack}>
        <div className="text-muted-text py-10 text-center text-[12px]">Issue not found or no longer accessible.</div>
      </DetailOverlay>
    )
  }
  return <IssueDetailLoaded issue={issue} {...props} />
}
